import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PresentSignal, SurfDeck } from "@vida/surf-core";
import {
  formatDurationSeconds,
  resolveSpeakerScriptConfig,
  resolveTargetSeconds,
} from "@vida/surf-core";
import { getPublicBaseUrl } from "../../config";
import { SlideCanvas } from "../editor/SlideCanvas";
import { SpeakerScriptView } from "../editor/SpeakerScriptEditor";
import { RemotePairing } from "./RemotePairing";
import { getOrCreatePresentSession } from "./presentSession";
import { useRemoteChannel } from "./useRemoteChannel";
import "./PresentMode.css";

const IDLE_MS = 2500;
/** Grace period before warning that the phone never connected (same-account/QR hint). */
const PEER_HINT_DELAY_MS = 6000;

type Props = {
  deck: SurfDeck;
  initialIndex: number;
  slideBgUrls: Record<string, string>;
  layerImageUrls: Record<string, string>;
  onClose: (finalIndex: number) => void;
};

function collectSlideImageUrls(
  slideId: string,
  slideBgUrls: Record<string, string>,
  layerImageUrls: Record<string, string>,
  deck: SurfDeck,
): string[] {
  const urls: string[] = [];
  const bg = slideBgUrls[slideId];
  if (bg) urls.push(bg);
  const slide = deck.slides.find((s) => s.id === slideId);
  if (!slide) return urls;
  for (const layer of slide.layers) {
    const url = layerImageUrls[layer.id];
    if (url) urls.push(url);
  }
  return urls;
}

function slideTitle(deck: SurfDeck, slideId: string): string {
  const slide = deck.slides.find((s) => s.id === slideId);
  const title = slide?.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim();
  return title || deck.title;
}

export function PresentMode({
  deck,
  initialIndex,
  slideBgUrls,
  layerImageUrls,
  onClose,
}: Props) {
  const total = deck.slides.length;
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(initialIndex, 0), Math.max(total - 1, 0)),
  );
  const [idle, setIdle] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [presenterOpen, setPresenterOpen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(false);
  const [elapsedSlide, setElapsedSlide] = useState(0);
  const [elapsedTotal, setElapsedTotal] = useState(0);
  // Session singleton per deck: eager al mount così il display sottoscrive il
  // canale appena entra in presentazione. Un telefono già abbinato in precedenza
  // riprende il controllo senza riaprire il QR; muore solo col reload della pagina.
  const [sessionId] = useState(() => getOrCreatePresentSession(deck.id));
  const [pairingOpen, setPairingOpen] = useState(false);
  const [blanked, setBlanked] = useState(false);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const lastSignalTsRef = useRef(0);
  const indexRef = useRef(index);
  indexRef.current = index;
  const containerRef = useRef<HTMLDivElement>(null);
  const teleprompterRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slideStartedRef = useRef(Date.now());
  const totalStartedRef = useRef(Date.now());

  const config = resolveSpeakerScriptConfig(deck);
  const slide = deck.slides[index];
  const nextSlide = index < total - 1 ? deck.slides[index + 1] : undefined;
  const atStart = index <= 0;
  const atEnd = index >= total - 1;
  const scriptText = slide?.speakerScript?.text?.trim() ?? "";
  const targetSeconds = slide ? resolveTargetSeconds(deck, slide.id) : 60;
  const timerRatio = targetSeconds > 0 ? elapsedSlide / targetSeconds : 0;
  const timerStatus = timerRatio > 1.5 ? "over" : timerRatio > 1 ? "warn" : "ok";

  const remoteUrl = useMemo(() => {
    if (!sessionId) return "";
    const base = getPublicBaseUrl();
    return `${base}/deck/${deck.id}/remote#s=${sessionId}`;
  }, [sessionId, deck.id]);

  const onRemoteSignal = useCallback(
    (signal: PresentSignal) => {
      // Anti-replay: server ts is monotonic; drop stale/duplicate/out-of-order events.
      if (signal.ts <= lastSignalTsRef.current) return;
      lastSignalTsRef.current = signal.ts;
      switch (signal.action) {
        case "goto":
          if (signal.slideIndex != null) {
            setIndex(Math.max(0, Math.min(total - 1, signal.slideIndex)));
          }
          break;
        case "blank":
          setBlanked(true);
          break;
        case "unblank":
          setBlanked(false);
          break;
        case "pointer":
          setPointer(
            signal.pointerX != null && signal.pointerY != null
              ? { x: signal.pointerX, y: signal.pointerY }
              : null,
          );
          break;
        default:
          break;
      }
    },
    [total],
  );

  const remote = useRemoteChannel({
    sessionId,
    deckId: deck.id,
    role: "display",
    onSignal: onRemoteSignal,
  });

  // After a grace period without hearing from the phone, hint at the likely cause
  // (different Cognito account / stale QR) instead of leaving the user waiting.
  const [peerHintVisible, setPeerHintVisible] = useState(false);
  const remoteStatus = remote.status;
  const remotePeerPresent = remote.peerPresent;
  useEffect(() => {
    if (!sessionId || remotePeerPresent) {
      setPeerHintVisible(false);
      return;
    }
    const timer = setTimeout(() => setPeerHintVisible(true), PEER_HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sessionId, remotePeerPresent, remoteStatus]);

  // Handshake: when the phone appears, announce our current slide so it aligns.
  const peerPresent = remote.peerPresent;
  const remoteSendPing = remote.sendPing;
  useEffect(() => {
    if (sessionId && peerPresent) {
      remoteSendPing(indexRef.current);
    }
  }, [sessionId, peerPresent, remoteSendPing]);

  const openPairing = useCallback(() => setPairingOpen(true), []);

  const closePairing = useCallback(() => setPairingOpen(false), []);

  // C: auto-chiusura del dialog QR alla connessione del telefono, solo sulla
  // transizione peerPresent false→true mentre il dialog è aperto (riaprirlo con
  // phone già connesso lo lascia aperto). Delay per far vedere il badge verde.
  const prevPeerRef = useRef(remotePeerPresent);
  useEffect(() => {
    const was = prevPeerRef.current;
    prevPeerRef.current = remotePeerPresent;
    if (!pairingOpen || was || !remotePeerPresent) return;
    const t = setTimeout(() => setPairingOpen(false), 900);
    return () => clearTimeout(t);
  }, [remotePeerPresent, pairingOpen]);

  const bumpActivity = useCallback(() => {
    setIdle(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIdle(true), IDLE_MS);
  }, []);

  useEffect(() => {
    bumpActivity();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [bumpActivity]);

  useEffect(() => {
    slideStartedRef.current = Date.now();
    setElapsedSlide(0);
  }, [index]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setElapsedSlide(Math.floor((Date.now() - slideStartedRef.current) / 1000));
      setElapsedTotal(Math.floor((Date.now() - totalStartedRef.current) / 1000));
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!autoScroll || !teleprompterRef.current || !scriptText) return;
    const wpm = config.wordsPerMinute;
    const pxPerSecond = (wpm / 60) * 2.8;
    const id = window.setInterval(() => {
      const el = teleprompterRef.current;
      if (el) el.scrollTop += pxPerSecond * 0.25;
    }, 250);
    return () => window.clearInterval(id);
  }, [autoScroll, config.wordsPerMinute, scriptText]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });

    const trapTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = el.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    el.addEventListener("keydown", trapTab);
    return () => el.removeEventListener("keydown", trapTab);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    if (document.fullscreenElement === containerRef.current) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const preload = (slideIndex: number) => {
      const s = deck.slides[slideIndex];
      if (!s) return;
      for (const url of collectSlideImageUrls(s.id, slideBgUrls, layerImageUrls, deck)) {
        const img = new Image();
        img.src = url;
      }
    };
    if (index > 0) preload(index - 1);
    if (index < total - 1) preload(index + 1);
  }, [index, deck, slideBgUrls, layerImageUrls, total]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
    bumpActivity();
  }, [bumpActivity]);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(total - 1, i + 1));
    bumpActivity();
  }, [bumpActivity, total]);

  const remoteSendEnd = remote.sendEnd;
  const exitPresent = useCallback(() => {
    if (sessionId) remoteSendEnd();
    if (document.fullscreenElement) {
      void document.exitFullscreen().finally(() => onClose(index));
    } else {
      onClose(index);
    }
  }, [index, onClose, sessionId, remoteSendEnd]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen().catch(() => undefined);
    }
    bumpActivity();
  }, [bumpActivity]);

  const togglePresenter = useCallback(() => {
    setPresenterOpen((v) => !v);
    bumpActivity();
  }, [bumpActivity]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      bumpActivity();
      switch (e.key) {
        case "ArrowRight":
        case "PageDown":
          e.preventDefault();
          if (!atEnd) goNext();
          break;
        case " ":
          e.preventDefault();
          if (!atEnd) goNext();
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          if (!atStart) goPrev();
          break;
        case "Home":
          e.preventDefault();
          setIndex(0);
          break;
        case "End":
          e.preventDefault();
          setIndex(total - 1);
          break;
        case "Escape":
          e.preventDefault();
          exitPresent();
          break;
        case "f":
        case "F":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "n":
        case "N":
        case "s":
        case "S":
          e.preventDefault();
          togglePresenter();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [atEnd, atStart, bumpActivity, exitPresent, goNext, goPrev, toggleFullscreen, togglePresenter, total]);

  if (!slide) return null;

  const className = [
    "present-mode",
    presenterOpen ? "present-mode--presenter" : "",
    idle ? "present-mode--idle" : "",
    fullscreen ? "present-mode--fullscreen" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const remaining = Math.max(0, targetSeconds - elapsedSlide);

  return (
    <div
      ref={containerRef}
      className={className}
      role="application"
      aria-label="Modalità presentazione"
      tabIndex={-1}
      onMouseMove={bumpActivity}
      onTouchStart={bumpActivity}
    >
      <div className="present-mode-main">
        <div className="present-mode-stage present-mode-canvas">
          <SlideCanvas
            deck={deck}
            slide={slide}
            slideBackgroundUrl={slideBgUrls[slide.id] ?? null}
            layerImageUrls={layerImageUrls}
            selectedLayerIds={[]}
            showGuides={false}
            readOnly
            onSelectLayer={() => undefined}
            onLayerRect={() => undefined}
            onLayerContent={() => undefined}
          />
        </div>

        {presenterOpen && (
          <aside className={`present-mode-presenter present-mode-presenter--${timerStatus}`}>
            <div className="present-mode-presenter-timers">
              <span>
                Slide: {formatDurationSeconds(elapsedSlide)} / {formatDurationSeconds(targetSeconds)}
              </span>
              <span>−{formatDurationSeconds(remaining)}</span>
              <span>Totale: {formatDurationSeconds(elapsedTotal)}</span>
            </div>
            <div ref={teleprompterRef} className="present-mode-teleprompter">
              {scriptText ? (
                <SpeakerScriptView markdown={scriptText} className="present-mode-teleprompter-md" />
              ) : (
                <p className="muted">Nessun discorso speaker per questa slide.</p>
              )}
            </div>
            {nextSlide && (
              <div className="present-mode-next">
                <span className="muted">Prossima</span>
                <strong>{slideTitle(deck, nextSlide.id)}</strong>
              </div>
            )}
            <div className="present-mode-presenter-actions">
              <button
                type="button"
                className="surf-btn surf-btn--ghost"
                onClick={() => setAutoScroll((v) => !v)}
              >
                {autoScroll ? "Pausa scroll" : "Auto-scroll"}
              </button>
            </div>
          </aside>
        )}
      </div>

      <div className="present-mode-controls" aria-hidden={idle || fullscreen}>
        <div className="present-mode-toolbar">
          <button
            type="button"
            className="surf-btn surf-btn--ghost"
            aria-label={presenterOpen ? "Chiudi presenter" : "Apri presenter"}
            title="Presenter (N/S)"
            onClick={togglePresenter}
          >
            {presenterOpen ? "◧" : "▤"}
          </button>
          <button
            type="button"
            className="surf-btn surf-btn--ghost"
            aria-label={fullscreen ? "Esci da schermo intero" : "Schermo intero"}
            title={fullscreen ? "Esci da schermo intero (F)" : "Schermo intero (F)"}
            onClick={toggleFullscreen}
          >
            {fullscreen ? "⤢" : "⛶"}
          </button>
          <button
            type="button"
            className={`surf-btn surf-btn--ghost${remote.peerPresent ? " is-active" : ""}`}
            aria-label="Telecomando"
            title="Telecomando (phone)"
            onClick={openPairing}
          >
            {remote.peerPresent ? "📱●" : "📱"}
          </button>
          <button
            type="button"
            className="surf-btn surf-btn--ghost"
            aria-label="Chiudi presentazione"
            title="Chiudi (Esc)"
            onClick={exitPresent}
          >
            ×
          </button>
        </div>

        <button
          type="button"
          className="present-mode-nav present-mode-nav--prev"
          aria-label="Slide precedente"
          disabled={atStart}
          onClick={goPrev}
        >
          ◀
        </button>
        <button
          type="button"
          className="present-mode-nav present-mode-nav--next"
          aria-label="Slide successiva"
          disabled={atEnd}
          onClick={goNext}
        >
          ▶
        </button>

        <div className="present-mode-bottom">
          <span className="present-mode-counter" aria-hidden>
            {index + 1} / {total}
          </span>
        </div>
      </div>

      {sessionId && (remote.lastError || (peerHintVisible && !remote.peerPresent)) && (
        <div className="present-mode-remote-diag" role="status" aria-live="polite">
          {remote.lastError ? (
            <span className="present-mode-remote-diag-line present-mode-remote-diag-line--error">
              Errore canale: {remote.lastError}
            </span>
          ) : (
            <span className="present-mode-remote-diag-line">
              Telecomando non raggiunto — verifica di aver aperto il QR con lo <strong>stesso account</strong>.
            </span>
          )}
        </div>
      )}

      {pointer && (
        <div
          className="present-mode-pointer"
          aria-hidden
          style={{ left: `${pointer.x * 100}%`, top: `${pointer.y * 100}%` }}
        />
      )}

      {blanked && (
        <div className="present-mode-blank" aria-hidden onClick={() => setBlanked(false)} />
      )}

      {pairingOpen && sessionId && (
        <RemotePairing
          remoteUrl={remoteUrl}
          sessionId={sessionId}
          peerPresent={remote.peerPresent}
          onClose={closePairing}
        />
      )}

      <p className="present-mode-sr-only" aria-live="polite" aria-atomic="true">
        Slide {index + 1} di {total}
      </p>
    </div>
  );
}
